/**
 * How old the data on screen is.
 *
 * A polled table looks identical whether it refreshed a second ago or stopped
 * refreshing ten minutes ago, and this panel is watched while hardware is being
 * power-cycled — "did it come back" is only answerable if the operator can tell
 * live data from a frozen page. So every polled view states its own age, and says
 * plainly when the updates have stopped arriving.
 *
 * Finer-grained than `format.ts`'s `since()` on purpose. That one is deliberately
 * coarse because a row's heartbeat age is compared against a 300 s interval; this
 * one is compared against a 10 s poll, where seconds are the unit that matters.
 */

/** Under this, the data is current enough that a number would be noise. */
const JUST_NOW_MS = 5_000;

/** Three missed polls, matching the heartbeat's three-missed-beats rule. */
const STALLED_INTERVALS = 3;

/**
 * `updatedAt` is React Query's `dataUpdatedAt`: milliseconds, or 0 when no fetch
 * has ever succeeded. `now` is a parameter so this is testable without faking the
 * clock.
 */
export function freshnessAge(updatedAt: number, now: number = Date.now()): string {
  if (updatedAt === 0) return "not loaded";

  const seconds = Math.floor((now - updatedAt) / 1000);
  // A corrected browser clock produces a negative age. The data is current either
  // way, and "in 3s" would be a caption nobody can act on.
  if (seconds < JUST_NOW_MS / 1000) return "just now";
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  return `${Math.floor(minutes / 60)}h ago`;
}

/**
 * Whether the feed has stopped updating. True after three intervals with nothing
 * new — which is the normal state of a backgrounded tab, because React Query
 * suspends polling when the window loses focus. That is worth showing rather than
 * hiding: a page left open on a second monitor is exactly the one an operator will
 * misread as live.
 */
export function isFeedStalled(
  updatedAt: number,
  now: number = Date.now(),
  intervalMs: number,
): boolean {
  if (updatedAt === 0) return false;

  return now - updatedAt > intervalMs * STALLED_INTERVALS;
}
